import { useMemo, useState } from "react";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { RELATIONSHIP_TYPES } from "@tellescope/constants";
import type { EnduserRelationship } from "@tellescope/types-models";
import type { UseRelatedContactsBlockReturn } from "../../../hooks";

export type UseRelatedContactsAddViewArgs = Pick<
  UseRelatedContactsBlockReturn,
  "enduserId" | "relationshipIds" | "updateEnduserMutation" | "closeAddView"
>;

const toRelationshipType = (value: string): EnduserRelationship["type"] | undefined =>
  RELATIONSHIP_TYPES.find((type) => type === value);

export const useRelatedContactsAddView = ({
  enduserId,
  relationshipIds,
  updateEnduserMutation,
  closeAddView,
}: UseRelatedContactsAddViewArgs) => {
  const [addRelationship, setAddRelationship] = useState("");
  const [addSearchQuery, setAddSearchQuery] = useState("");
  const [addSelectedContactId, setAddSelectedContactId] = useState("");
  const [debouncedAddSearchQuery] = useDebouncedValue(addSearchQuery);

  const availableContactsQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      search: debouncedAddSearchQuery ? { query: debouncedAddSearchQuery } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const filteredAddContacts = useMemo(() => {
    if (!availableContactsQuery.data) return [];
    return availableContactsQuery.data.filter((c) => c.id !== enduserId && !relationshipIds.includes(c.id));
  }, [availableContactsQuery.data, enduserId, relationshipIds]);

  const canAddContact = Boolean(addSelectedContactId && addRelationship);

  const addRelatedContact = () => {
    const relationshipType = toRelationshipType(addRelationship);
    if (!addSelectedContactId || !relationshipType) return;

    // replaceObjectFields: false triggers $addToSet (no duplicates); the backend creates the
    // bidirectional side automatically.
    updateEnduserMutation.mutate(
      {
        id: enduserId,
        relationships: [{ id: addSelectedContactId, type: relationshipType }],
        _options: { replaceObjectFields: false },
      },
      { onSuccess: closeAddView }
    );
  };

  return {
    relationshipTypes: RELATIONSHIP_TYPES,
    addRelationship,
    setAddRelationship,
    addSearchQuery,
    setAddSearchQuery,
    addSelectedContactId,
    setAddSelectedContactId,
    filteredAddContacts,
    canAddContact,
    addRelatedContact,
    isLoading: availableContactsQuery.isLoading,
    isAdding: updateEnduserMutation.isPending,
  };
};
